/**
 * Size Chart Integration with Existing Dialog System
 * This works with the existing dialog-component and dialog.js
 */

import { CartAddEvent } from '@theme/events';

class SizeChartIntegration {
  constructor() {
    this.isModalOpen = false;
    
    // Old hardcoded size chart data - now using dynamic generation instead
    /*
    this.sizeChartData = {
      women_topwear_body: [
        { size: 'XXS', bust: '31-32', waist: '27-28', hips: '34-35.5' },
        { size: 'XS', bust: '33-34', waist: '29-30', hips: '36-37.5' },
        { size: 'S', bust: '35-36', waist: '31-32.5', hips: '38-39' },
        { size: 'M', bust: '37-38', waist: '33.5-35', hips: '40.5-42' },
        { size: 'L', bust: '39-40', waist: '35.5-37', hips: '42.5-44' },
        { size: 'XL', bust: '41-42', waist: '37.5-39', hips: '44.5-46' },
        { size: '2XL', bust: '43-44', waist: '39.5-41', hips: '46.5-48' },
        { size: 'XXL', bust: '43-44', waist: '39.5-41', hips: '46.5-48' }
      ],
      men_topwear_body: [
        { size: 'XS', chest: '34-36', waist: '28-30', hips: '36-38' },
        { size: 'S', chest: '36-38', waist: '30-32', hips: '38-40' },
        { size: 'M', chest: '38-40', waist: '32-34', hips: '40-42' },
        { size: 'L', chest: '40-42', waist: '34-36', hips: '42-44' },
        { size: 'XL', chest: '42-44', waist: '36-38', hips: '44-46' },
        { size: '2XL', chest: '44-46', waist: '38-40', hips: '46-48' },
        { size: 'XXL', chest: '44-46', waist: '38-40', hips: '46-48' }
      ],
      women_bottom_wear_body: [
        { size: 'XXS', waist: '25-26', hips: '35-36', length: '38-39' },
        { size: 'XS', waist: '27-28', hips: '37-38', length: '39-40' },
        { size: 'S', waist: '29-30', hips: '39-40', length: '40-41' },
        { size: 'M', waist: '31-32', hips: '41-42', length: '41-42' },
        { size: 'L', waist: '33-34', hips: '43-44', length: '42-43' },
        { size: 'XL', waist: '35-36', hips: '45-46', length: '43-44' }
      ],
      loungewear_set: [
        { size: 'XS', bust: '32-34', waist: '28-30', hips: '35-37' },
        { size: 'S', bust: '34-36', waist: '30-32', hips: '37-39' },
        { size: 'M', bust: '36-38', waist: '32-34', hips: '39-41' },
        { size: 'L', bust: '38-40', waist: '34-36', hips: '41-43' },
        { size: 'XL', bust: '40-42', waist: '36-38', hips: '43-45' }
      ],
      men_bottom_wear_body: [
        { size: 'XS', waist: '28-30', hips: '36-38', length: '40-41' },
        { size: 'S', waist: '30-32', hips: '38-40', length: '41-42' },
        { size: 'M', waist: '32-34', hips: '40-42', length: '42-43' },
        { size: 'L', waist: '34-36', hips: '42-44', length: '43-44' },
        { size: 'XL', waist: '36-38', hips: '44-46', length: '44-45' }
      ],
      farsi_pants: [
        { size: 'XS', waist: '26-28', hips: '36-38', length: '38-40' },
        { size: 'S', waist: '28-30', hips: '38-40', length: '40-42' },
        { size: 'M', waist: '30-32', hips: '40-42', length: '42-44' },
        { size: 'L', waist: '32-34', hips: '42-44', length: '44-46' },
        { size: 'XL', waist: '34-36', hips: '44-46', length: '46-48' }
      ]
    };
    */
    
    this.init();
  }

  /**
   * Reset modal UI state so reopening is instantly usable.
   * - Clears selected row/radio
   * - Resets ATC buttons text/loading/styles and disables until a size is selected
   * @param {HTMLElement} modal
   */
  resetModalUI(modal) {
    if (!modal) return;

    // Clear selection state
    modal.querySelectorAll('tbody tr.selected').forEach((row) => row.classList.remove('selected'));
    modal.querySelectorAll('input[name^="size-selection"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });

    // Reset all add-to-cart buttons
    const buttons = modal.querySelectorAll('[id^="size-chart-add-to-cart"]');
    buttons.forEach((btn) => {
      const text = btn.querySelector('.btn__text');
      const loading = btn.querySelector('.btn__loading');

      btn.disabled = true;
      if (text) {
        text.textContent = 'Add to Bag';
        text.style.display = 'block';
        text.style.color = '';
      }
      if (loading) loading.style.display = 'none';
      btn.style.backgroundColor = '';
    });
  }

  init() {

    
    // Listen for size chart button clicks
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-size-chart-open]') || e.target.closest('[data-size-chart-open]')) {
        e.preventDefault();
        const button = e.target.matches('[data-size-chart-open]') ? e.target : e.target.closest('[data-size-chart-open]');
        this.openSizeChart(button);
      }
    });

    // Listen for size selection in modal
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name^="size-selection"]')) {
        this.handleSizeSelection(e.target);
      }
    });

    // Listen for add to cart in modal
    document.addEventListener('click', (e) => {
      if (e.target.matches('[id^="size-chart-add-to-cart"]')) {
        e.preventDefault();
        this.handleAddToCart(e.target);
      }
    });
  }

  async openSizeChart(button) {
    // Prevent multiple modal openings
    if (this.isModalOpen) {
      return;
    }
    
    // Prevent rapid clicking
    button.disabled = true;
    setTimeout(() => {
      button.disabled = false;
    }, 500);
    
    const sizeChartType = button.dataset.sizeChartType;
    const productId = button.dataset.productId;
    
    // console.log('Opening size chart:', { sizeChartType, productId });
    
    try {
      this.isModalOpen = true;
      // Find the modal for this product
      const modalId = `size-chart-modal-${productId}`;
      const modal = document.getElementById(modalId);
      
      if (!modal) {
        console.error('Modal not found:', modalId);
        return;
      }

      // Get product data - try to get from existing product object first
      let product = this.getProductFromPage(productId);
      
      if (!product) {
        // Fallback to API call if product not found on page
        product = await this.fetchProductData(productId);
      }
      
      // Populate size chart table
      this.populateSizeChart(modal, sizeChartType, product);
      // Ensure modal UI is in a clean state on every open
      this.resetModalUI(modal);
      
      // Open the modal using existing dialog system
      // `modal` itself is the <dialog-component id="size-chart-modal-...">
      const dialogComponent = modal;

      if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
        dialogComponent.showDialog();
      } else {
        // Fallback: open the native <dialog> directly if the component isn't upgraded
        const dialog = modal.querySelector('dialog');
        if (dialog) dialog.showModal();
      }
      
      
    } catch (error) {
      console.error('Error opening size chart:', error);
      alert(`Unable to load size chart: ${error.message}`);
      this.isModalOpen = false; // Reset flag on error
    }
  }

  getProductFromPage(productId) {
    
    // Method 1: Check for product data in specific script tag
    const productScript = document.querySelector(`script[id="product-data-${productId}"]`);
    if (productScript) {
      try {
        
        const productData = JSON.parse(productScript.textContent);
        if (productData && productData.id) {
          return productData;
        }
      } catch (e) {
        // Failed to parse product script
                console.log('Could not parse product script:', e.message);
        console.log('Script content:', productScript.textContent);
      }
    }
    
    // Method 2: Check for general product data in script tags
    const generalProductScript = document.querySelector('script[type="application/json"]');
    if (generalProductScript) {
      try {
        const productData = JSON.parse(generalProductScript.textContent);
        if (productData && productData.id) {
          return productData;
        }
      } catch (e) {
        // Failed to parse general product script
         console.log('Could not parse general product script');
      }
    }
    
    // Method 3: Check for global product variable
    if (window.product && window.product.id) {
      return window.product;
    }
    
    // Method 4: Build product data from page elements
    const productTitle = document.querySelector('h1')?.textContent || 'Product';
    const productPriceElement = document.querySelector('.price, [class*="price"]');
    const productPrice = productPriceElement ? this.parsePrice(productPriceElement.textContent) : 0;
    
    // Get variants from variant picker
    const variants = this.getVariantsFromPage();
    
    const productData = {
      id: parseInt(productId),
      title: productTitle,
      price: productPrice,
      compare_at_price: 0,
      featured_image: null,
      variants: variants
    };

    return productData;
  }

  parsePrice(priceText) {
    // Simple price parsing - extract number from price text
    const match = priceText.match(/[\d,]+/);
    return match ? parseInt(match[0].replace(/,/g, '')) * 100 : 0; // Convert to cents
  }

  getVariantsFromPage() {
    // Try to get variants from the page
    const variantInputs = document.querySelectorAll('variant-picker input[type="radio"]');
    const variants = [];
    
    
    variantInputs.forEach((input, index) => {
      if (input.dataset.variantId) {
        const variant = {
          id: parseInt(input.dataset.variantId),
          price: this.parsePrice(input.dataset.variantPrice || '0'),
          available: input.dataset.optionAvailable !== 'false',
          options: [input.value]
        };
        variants.push(variant);
      }
    });
    
    // If no variants found from inputs, try to get from select elements
    if (variants.length === 0) {
      const variantSelects = document.querySelectorAll('variant-picker select');
      variantSelects.forEach(select => {
        Array.from(select.options).forEach(option => {
          if (option.dataset.variantId) {
            const variant = {
              id: parseInt(option.dataset.variantId),
              price: this.parsePrice(option.dataset.variantPrice || '0'),
              available: !option.textContent.includes('unavailable'),
              options: [option.value]
            };
            variants.push(variant);
            // console.log('Added variant from select:', variant);
          }
        });
      });
    }
    
    return variants;
  }

  async fetchProductData(productId) {
    const response = await fetch(`/products/${productId}.js`);
    if (!response.ok) {
      throw new Error(`Product not found: ${response.status}`);
    }
    return await response.json();
  }

  populateSizeChart(modal, sizeChartType, product) {
    const tbody = modal.querySelector('tbody');
    
    if (!tbody) {
      console.error('No tbody found in modal');
      return;
    }

    // Get unique sizes from product variants
    const availableSizes = product.variants.map(v => v.options[0]).filter((size, index, arr) => arr.indexOf(size) === index);
    // console.log('Available sizes from product:', availableSizes);
    
    // Generate dynamic size chart data based on actual variants
    const dynamicChartData = this.generateDynamicSizeChart(availableSizes, sizeChartType, product);
    
    // Populate table headers dynamically from variant metafields
    const thead = modal.querySelector('thead tr');
    const existingHeaders = thead.querySelectorAll('th');
    
    // Get headers from the dynamic chart data
    const headers = Object.keys(dynamicChartData[0] || {}).filter(key => key !== 'size');
    
    // Remove existing dynamic headers (keep SELECT and SIZE)
    existingHeaders.forEach((th, index) => {
      if (index > 1) {
        th.remove();
      }
    });
    
    // Add new headers based on variant metafields
    headers.forEach(headerKey => {
      const th = document.createElement('th');
      th.textContent = this.getColumnLabel(headerKey);
      thead.appendChild(th);
    });
    
    tbody.innerHTML = '';
    
    dynamicChartData.forEach((sizeData) => {
      const row = document.createElement('tr');
      row.dataset.size = sizeData.size;
      
      // Check if this variant is available
      // If size from metaobject doesn't have a matching variant, mark as unavailable
      const variant = product.variants.find(v => v.options[0] === sizeData.size);
      const isAvailable = variant ? variant.available : false;
      
      if (!isAvailable) {
        row.classList.add('disabled');
      }
      
      // Create radio button cell
      const radioCell = document.createElement('td');
      const radioInput = document.createElement('input');
      radioInput.type = 'radio';
      radioInput.name = `size-selection-${product.id}`;
      radioInput.value = sizeData.size;
      radioInput.id = `size-${sizeData.size}-${product.id}`;
      radioInput.disabled = !isAvailable;
      radioCell.appendChild(radioInput);
      
      // Create size cell
      const sizeCell = document.createElement('td');
      sizeCell.textContent = sizeData.size;
      
      // Create measurement cells
      const measurementCells = Object.keys(sizeData)
        .filter(key => key !== 'size')
        .map(key => {
          const cell = document.createElement('td');
          cell.textContent = sizeData[key] || '-';
          return cell;
        });

      row.appendChild(radioCell);
      row.appendChild(sizeCell);
      
      // Append measurement cells one by one to avoid spread operator issues
      measurementCells.forEach(cell => {
        row.appendChild(cell);
      });
      
      // Add click handler to row (only if available)
      if (isAvailable) {
        row.addEventListener('click', () => {
          radioInput.checked = true;
          this.handleSizeSelection(radioInput);
        });
      }

      tbody.appendChild(row);
    });

    // Update all add to cart buttons
    const addToCartBtns = modal.querySelectorAll('[id^="size-chart-add-to-cart"]');
    
    addToCartBtns.forEach(btn => {
      btn.disabled = true;
      const text = btn.querySelector('.btn__text');
      const loading = btn.querySelector('.btn__loading');
      if (text) {
        text.textContent = 'Add to Bag';
        text.style.display = 'block';
        text.style.color = '';
      }
      if (loading) loading.style.display = 'none';
      btn.style.backgroundColor = '';
    });
    
    
    // Initialize close handlers
    this.initializeCloseHandlers(modal);
  }

  generateDynamicSizeChart(availableSizes, sizeChartType, product) {
    // console.log('Generating dynamic size chart for:', availableSizes, sizeChartType);
    
    // Sort sizes in a logical order
    const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', '4XL'];
    const sortedSizes = availableSizes.sort((a, b) => {
      const aIndex = sizeOrder.indexOf(a.toUpperCase());
      const bIndex = sizeOrder.indexOf(b.toUpperCase());
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
    
    // Get size chart data from metaobject (new method)
    const metaobjectData = this.getSizeChartFromMetaobject(product, sortedSizes);
    if (metaobjectData && metaobjectData.columns.length > 0) {
      return metaobjectData.rows;
    }
    
    // Fallback to variant metafields for backward compatibility
    const variantMetafieldData = this.getSizeChartFromVariantMetafields(product, sortedSizes);
    if (variantMetafieldData && variantMetafieldData.columns.length > 0) {
      return variantMetafieldData.rows;
    }
    
    // No fallback - return empty data if no data found
    console.log('No size chart data found - returning empty size chart');
    return sortedSizes.map(size => ({ size: size }));
  }

  getMeasurementsForType(sizeChartType) {
    if (!sizeChartType) {
      return null;
    }
    
    const measurementTypes = {
      women_topwear_body: { bust: 2, waist: 1.5, hips: 2 },
      men_topwear_body: { chest: 2, waist: 2, hips: 2 },
      women_bottom_wear_body: { waist: 2, hips: 2, length: 1 },
      loungewear_set: { bust: 2, waist: 1.5, hips: 2 },
      men_bottom_wear_body: { waist: 2, hips: 2, length: 1 },
      farsi_pants: { waist: 2, hips: 2, length: 1 }
    };
    
    return measurementTypes[sizeChartType] || null;
  }

  getBaseMeasurements(sizeChartType) {
    if (!sizeChartType) {
      return null;
    }
    
    const baseMeasurements = {
      women_topwear_body: { bust: 33, waist: 27, hips: 35 },
      men_topwear_body: { chest: 34, waist: 28, hips: 36 },
      women_bottom_wear_body: { waist: 25, hips: 35, length: 38 },
      loungewear_set: { bust: 33, waist: 27, hips: 35 },
      men_bottom_wear_body: { waist: 28, hips: 36, length: 40 },
      farsi_pants: { waist: 28, hips: 36, length: 40 }
    };
    
    return baseMeasurements[sizeChartType] || null;
  }

  formatMeasurement(value, increment) {
    // Format as range (e.g., "35-36" for bust measurements)
    const lower = Math.round(value);
    const upper = Math.round(value + increment);
    return `${lower}-${upper}`;
  }

  handleSizeSelection(radioInput) {
    const row = radioInput.closest('tr');
    const modal = radioInput.closest('dialog-component');
    
    // Remove previous selection
    const previousSelected = modal.querySelector('tbody tr.selected');
    if (previousSelected) {
      previousSelected.classList.remove('selected');
    }
    
    // Add selection to current row
    if (row) {
      row.classList.add('selected');
    }
    
    // Enable both add to cart buttons
    const addToCartBtns = modal.querySelectorAll('[id^="size-chart-add-to-cart"]');
    addToCartBtns.forEach(btn => {
      btn.disabled = false;
    });
  }

  async handleAddToCart(button) {
    const modal = button.closest('dialog-component');
    const selectedSize = modal.querySelector('input[name^="size-selection"]:checked');
    
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }

    // Get all add to cart buttons in the modal
    const allButtons = modal.querySelectorAll('[id^="size-chart-add-to-cart"]');
    
    // Show loading state on all buttons
    allButtons.forEach(btn => {
      const text = btn.querySelector('.btn__text');
      const loading = btn.querySelector('.btn__loading');
      btn.disabled = true;
      text.style.display = 'none';
      loading.style.display = 'block';
    });

    try {
      // Get product ID from modal
      const modalId = modal.id;
      const productId = modalId.replace('size-chart-modal-', '');
      
      // Find matching variant using cached product data
      const product = this.getProductFromPage(productId);
      // console.log('Available variants:', product.variants.map(v => ({ id: v.id, options: v.options })));
      
      const matchingVariant = product.variants.find(variant => {
        const exactMatch = variant.options.some(option => 
          option.toLowerCase() === selectedSize.value.toLowerCase()
        );
        // console.log(`Variant ${variant.id} with options ${variant.options} matches "${selectedSize.value}":`, exactMatch);
        return exactMatch;
      });

      if (!matchingVariant) {
        throw new Error('No variant found for selected size');
      }

      // console.log('Found matching variant:', {
      //   id: matchingVariant.id,
      //   options: matchingVariant.options,
      //   price: matchingVariant.price
      // });

      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: matchingVariant.id,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Show success state on all buttons
      allButtons.forEach(btn => {
        const btnText = btn.querySelector('.btn__text');
        const btnLoading = btn.querySelector('.btn__loading');
        btnText.textContent = 'Added to Bag!';
        btnText.style.display = 'block';
        btnLoading.style.display = 'none';
        btn.style.backgroundColor = '#B07c83';
        btnText.style.color = "white";
      });
      
      // Dispatch cart update event with proper structure
      // Using 'product-form-component' as source so the counter adds to current count
      const buttonId = button.id || `size-chart-add-to-cart-${productId}`;
      document.dispatchEvent(
        new CartAddEvent({}, buttonId, {
          source: 'product-form-component',
          itemCount: 1,
          productId: productId,
          variantId: matchingVariant.id.toString(),
        })
      );
      
      // Close modal after a short delay (enough for feedback, but keeps UX snappy)
      setTimeout(() => {
        this.closeModal(modal);
      }, 350);

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Reset all buttons state
      allButtons.forEach(btn => {
        const btnText = btn.querySelector('.btn__text');
        const btnLoading = btn.querySelector('.btn__loading');
        btn.disabled = false;
        btnText.textContent = 'Add to Bag';
        btnText.style.display = 'block';
        btnLoading.style.display = 'none';
        btn.style.backgroundColor = '';
      });
      
      alert(`Failed to add to cart: ${error.message}`);
    }
  }

  // Add close modal functionality
  closeModal(modal) {
    if (!modal) return;
    // Prefer the theme's dialog-component close for proper body scroll restore + animations
    if (typeof modal.closeDialog === 'function') {
      modal.closeDialog();
      return;
    }

    const dialog = modal.querySelector('dialog');
    dialog?.close();
  }

  // Initialize close button handlers
  initializeCloseHandlers(modal) {
    // Bind handlers only once per modal to avoid duplicated listeners over time
    if (modal?.dataset?.sizeChartHandlersInitialized === 'true') return;
    if (modal?.dataset) modal.dataset.sizeChartHandlersInitialized = 'true';

    // Close button (top "×" in size guide header)
    const closeBtn = modal.querySelector('.size-chart-modal__close-button') || modal.querySelector('.close-button');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeModal(modal);
      });
    }

    // Rely on dialog-component built-in outside-click + Escape handling when available.
    const dialog = modal.querySelector('dialog');

    // Listen for dialog close event to ensure proper cleanup
    if (dialog) {
      dialog.addEventListener('close', () => {
        // Reset UI and unlock future opens immediately
        this.resetModalUI(modal);
        this.isModalOpen = false;
      });
    }
  }

  // Metaobject-based size chart method (new primary method)
  // size_chart_data is now a single JSON object: { "S": {...}, "M": {...}, "L": {...} }
  getSizeChartFromMetaobject(product, sortedSizes) {
    // Debug logging
    console.log('getSizeChartFromMetaobject called', {
      hasSizeChartData: !!product.size_chart_data,
      sizeChartDataType: typeof product.size_chart_data,
      sizeChartData: product.size_chart_data,
      debug: product._debug
    });
    
    // Check if product has size_chart_data from metaobject
    if (!product.size_chart_data || 
        typeof product.size_chart_data !== 'object' || 
        product.size_chart_data === null ||
        Array.isArray(product.size_chart_data)) {
      console.log('Size chart data is invalid or missing');
      return null;
    }
    
    // Get all measurement columns that have data
    const allColumns = new Set();
    const sizeDataMap = {};
    const metaobjectSizes = [];
    
    // Process the sizes_data object (keys are sizes, values are measurements)
    Object.keys(product.size_chart_data).forEach(size => {
      const measurements = product.size_chart_data[size];
      
      // Check if measurements is a valid object
      if (measurements && 
          typeof measurements === 'object' && 
          measurements !== null && 
          !Array.isArray(measurements)) {
        
        // Store size data
        sizeDataMap[size] = measurements;
        metaobjectSizes.push(size);
        
        // Collect all measurement keys that have values
        Object.keys(measurements).forEach(key => {
          const value = measurements[key];
          if (value && 
              value !== null && 
              value !== '' && 
              typeof value === 'string' &&
              value !== 'json not allowed for this object') {
            allColumns.add(key);
          }
        });
      }
    });
    
    if (allColumns.size === 0 || metaobjectSizes.length === 0) {
      console.log('No valid measurements found in metaobject data');
      return null;
    }
    
    // Create column definitions
    const columns = Array.from(allColumns).map(key => ({
      key: key,
      label: this.getColumnLabel(key),
      unit: 'inches'
    }));
    
    // Sort metaobject sizes in logical order
    const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', '4XL'];
    const sortedMetaobjectSizes = metaobjectSizes.sort((a, b) => {
      const aIndex = sizeOrder.indexOf(a.toUpperCase());
      const bIndex = sizeOrder.indexOf(b.toUpperCase());
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
    
    // Create rows for all sizes from metaobject (not just product variants)
    const rows = sortedMetaobjectSizes.map(size => {
      const sizeData = { size: size };
      
      columns.forEach(column => {
        const measurements = sizeDataMap[size];
        sizeData[column.key] = measurements && measurements[column.key] ? measurements[column.key] : '-';
      });
      
      return sizeData;
    });
    
    return {
      columns: columns,
      rows: rows,
      hasData: true
    };
  }

  // Variant-level metafield methods (kept for backward compatibility)
  getSizeChartFromVariantMetafields(product, sortedSizes) {
    
    // Get all measurement columns that have data
    const allColumns = new Set();
    const variantData = {};
    
    // Process each variant
    product.variants.forEach(variant => {
      const size = variant.options[0];
      const measurements = variant.metafields?.custom?.size_measurements;
  
      
      // Check if measurements is a valid object
      if (measurements && 
          typeof measurements === 'object' && 
          measurements !== null && 
          !Array.isArray(measurements)) {
        
        // console.log(`Variant ${size} has valid metafields:`, measurements);
        variantData[size] = measurements;
        
        // Collect all measurement keys that have values
        Object.keys(measurements).forEach(key => {
          const value = measurements[key];
          if (value && 
              value !== null && 
              value !== '' && 
              typeof value === 'string' &&
              value !== 'json not allowed for this object') {
            allColumns.add(key);
          }
        });
        } else {
        console.log(`Variant ${size} has no valid metafields or invalid format:`, measurements);
      }
    });
    
    
    if (allColumns.size === 0) {
       console.log('No variant metafields found, using fallback');
      return null;
    }
    
    // Create column definitions
    const columns = Array.from(allColumns).map(key => ({
      key: key,
      label: this.getColumnLabel(key),
      unit: 'inches'
    }));
    
    // Create rows for available sizes
    const rows = sortedSizes.map(size => {
      const sizeData = { size: size };
      
      columns.forEach(column => {
        const variant = variantData[size];
        sizeData[column.key] = variant && variant[column.key] ? variant[column.key] : '-';
      });
      
      return sizeData;
    });
    
    return {
      columns: columns,
      rows: rows,
      hasData: true
    };
  }

  getColumnLabel(key) {
    const labels = {
      'bust': 'BUST',
      'chest': 'CHEST', 
      'waist': 'WAIST',
      'hips': 'HIPS',
      'length': 'LENGTH',
      'inseam': 'INSEAM',
      'shoulder': 'SHOULDER',
      'sleeve': 'SLEEVE',
      'neck': 'NECK',
      'uk_size': 'UK SIZE',
      'us_size': 'US SIZE',
      'eu_size': 'EU SIZE'
    };
    
    return labels[key] || key.toUpperCase();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.sizeChartIntegration = new SizeChartIntegration();
});
